import React, { useState } from 'react';
import {
  Avatar, Box, Button, Card, CardContent, Chip,
  IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { PublicRide } from '../../types';
import { tidyOriginalMessage } from '../../utils/messageDisplay';

interface RideCardProps {
  ride: PublicRide;
  unlocked?: {
    price?: string;
    contactName?: string;
    contactNumber?: string;
    originalMessage?: string;
  } | null;
  onUnlock?: () => void;
  onSave?: () => void;
  onUnsave?: () => void;
  onHide?: () => void;
  onUnhide?: () => void;
  onReport?: () => void;
  onDetails?: () => void;
  unlockDisabled?: boolean;
  actionDisabled?: boolean;
  creditsRemaining?: number | null;
  freeAccess?: boolean;
}

function isUrgent(dateStr: string) {
  return Date.now() - new Date(dateStr).getTime() < 20 * 60 * 1000;
}

function isNew(dateStr: string) {
  return Date.now() - new Date(dateStr).getTime() < 2 * 60 * 60 * 1000;
}

const DUTY_LABELS: Record<string, string> = {
  'one-way': 'One Way', 'oneway': 'One Way',
  'round-trip': 'Round Trip', 'roundtrip': 'Round Trip',
  'outstation': 'Outstation', 'local': 'Local',
};

function formatDuty(dt?: string): string | null {
  if (!dt) return null;
  return DUTY_LABELS[dt.toLowerCase()] ?? dt;
}

const SignalBars: React.FC<{ level: number; color: string }> = ({ level, color }) => (
  <Stack direction="row" alignItems="flex-end" sx={{ gap: '2px', height: 13 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Box key={i} sx={{ width: 3, height: 3 + i * 2, bgcolor: i <= level ? color : 'rgba(0,0,0,0.1)', borderRadius: '1px' }} />
    ))}
  </Stack>
);

function getMatchInfo(match: PublicRide['match'], priority: string) {
  if (match?.isMatch) {
    if (match.score >= 75) return { label: 'Likely to book', color: '#14a35b', level: 5 };
    if (priority === 'high') return { label: 'High demand', color: '#E8A000', level: 4 };
    if (match.score >= 50) return { label: 'Good match', color: '#0891b2', level: 3 };
    return { label: 'Good match', color: '#0891b2', level: 2 };
  }
  if (priority === 'high') return { label: 'High priority', color: '#E8A000', level: 3 };
  if (priority === 'normal') return { label: null, color: '#94a3b8', level: 2 };
  return { label: null, color: '#94a3b8', level: 1 };
}

const RideCard: React.FC<RideCardProps> = ({
  ride, unlocked, onUnlock, onSave, onUnsave, onHide, onUnhide,
  onReport, onDetails, unlockDisabled, actionDisabled, creditsRemaining,
  freeAccess = false,
}) => {
  const [copied, setCopied] = useState(false);

  const showPrice = unlocked?.price || ((freeAccess || !ride.sensitiveHidden) && ride.price);
  const urgent = isUrgent(ride.createdAt);
  const newLead = isNew(ride.createdAt);
  const contactUnlocked = Boolean(unlocked?.contactNumber);
  const isPremium = ride.priority === 'high';
  const dutyLabel = formatDuty(ride.dutyType);
  const matchInfo = getMatchInfo(ride.match, ride.priority);
  const expired = ride.status === 'expired';

  // Stop propagation so nested buttons don't trigger the card-level onClick
  const sp = (fn?: (...args: unknown[]) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn?.();
  };

  const handleCall = () => {
    if (unlocked?.contactNumber) window.location.href = `tel:${unlocked.contactNumber}`;
  };
  const handleWhatsApp = () => {
    if (unlocked?.contactNumber) {
      window.open(`https://wa.me/${unlocked.contactNumber.replace(/\D/g, '')}`, '_blank', 'noopener,noreferrer');
    }
  };
  const handleCopy = () => {
    if (unlocked?.contactNumber) {
      navigator.clipboard.writeText(unlocked.contactNumber).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const messageText = freeAccess
    ? unlocked?.originalMessage || ride.originalMessage || ride.maskedMessage
    : contactUnlocked ? unlocked?.originalMessage : ride.maskedMessage;
  const hasMessage = Boolean(messageText);

  // Route meta shown on the right: "3 AM · Innova" or "Flexible · Dzire"
  const routeMeta = [ride.time || 'Flexible', ride.vehicleType].filter(Boolean).join(' · ');

  // Price display: prefix ₹ only if starts with digit
  const priceDisplay = showPrice
    ? (String(showPrice).match(/^\d/) ? `₹${showPrice}` : String(showPrice))
    : 'Discuss';

  return (
    <Card
      variant="outlined"
      onClick={onDetails}
      sx={{
        opacity: expired ? 0.65 : 1,
        cursor: onDetails ? 'pointer' : 'default',
        borderRadius: 4,
        border: '1px solid',
        borderColor: expired ? 'rgba(0,0,0,0.08)'
          : urgent ? 'rgba(239,68,68,0.25)'
          : ride.match?.isMatch ? 'rgba(20,163,91,0.2)'
          : isPremium ? 'rgba(243,181,27,0.25)'
          : '#E5E5E5',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        '&:hover': {
          boxShadow: expired ? '0 1px 4px rgba(0,0,0,0.06)' : '0 4px 16px rgba(0,0,0,0.1)',
          transform: expired ? 'none' : 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: '13px !important', display: 'flex', flexDirection: 'column', gap: 1 }}>

        {/* ── Tags row ─────────────────────────────────────────────────── */}
        <Box display="flex" alignItems="center" gap={0.5}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" flex={1} alignItems="center">
            {!expired && isPremium && (
              <Chip label="PREMIUM" size="small"
                sx={{ bgcolor: '#F5EEFF', color: '#6A1B9A', border: '1px solid #D1A3FF', fontSize: 10, height: 20, fontWeight: 600 }}
              />
            )}
            {!expired && urgent && (
              <Chip label="🔥 HOT" size="small"
                sx={{ bgcolor: '#FFF3E0', color: '#E65100', border: '1px solid #FFB74D', fontSize: 10, height: 20, fontWeight: 600 }}
              />
            )}
            {!expired && !urgent && newLead && (
              <Chip label="NEW" size="small"
                sx={{ bgcolor: '#f0fdf4', color: '#15803d', border: '1px solid rgba(21,128,61,0.3)', fontSize: 10, height: 20, fontWeight: 600 }}
              />
            )}
            {expired && (
              <Chip label="Expired" size="small" sx={{ bgcolor: '#f5f5f5', color: '#9e9e9e', fontSize: 10, height: 20 }} />
            )}
            {ride.reported && (
              <Chip label="Reported" size="small" variant="outlined" color="warning" sx={{ fontSize: 10, height: 20 }} />
            )}
            {!expired && dutyLabel && (
              <Chip label={dutyLabel} size="small"
                sx={{ bgcolor: '#E8F5E9', color: '#1B5E20', border: '1px solid #A5D6A7', fontSize: 10, height: 20, fontWeight: 600 }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            {(onSave || onUnsave) && (
              <Tooltip title={ride.saved && onUnsave ? 'Unsave' : 'Save ride'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={sp(ride.saved && onUnsave ? onUnsave : onSave)}
                    disabled={actionDisabled || !(ride.saved ? onUnsave : onSave)}
                    sx={{ p: 0 }}
                  >
                    {ride.saved
                      ? <BookmarkIcon sx={{ fontSize: 15, color: 'secondary.main' }} />
                      : <BookmarkBorderIcon sx={{ fontSize: 15, color: '#999' }} />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onHide && !onUnhide && (
              <Tooltip title="Hide ride">
                <span>
                  <IconButton size="small" onClick={sp(onHide)} disabled={actionDisabled} sx={{ p: 0 }}>
                    <VisibilityOffIcon sx={{ fontSize: 15, color: '#999' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {onUnhide && (
              <Tooltip title="Unhide ride">
                <span>
                  <IconButton size="small" onClick={sp(onUnhide)} disabled={actionDisabled} sx={{ p: 0 }}>
                    <VisibilityIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Box>

        {/* ── Route row (single line with inline dots) ─────────────────── */}
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#2E7D32', flexShrink: 0 }} />
          <Typography fontWeight={600} sx={{ fontSize: 15, color: '#111', lineHeight: 1.2, flexShrink: 0, maxWidth: '32%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ride.from || 'Pickup'}
          </Typography>
          <Typography sx={{ color: '#bbb', fontSize: 12, lineHeight: 1, flexShrink: 0 }}>→</Typography>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#C62828', flexShrink: 0 }} />
          <Typography fontWeight={600} sx={{ fontSize: 15, color: '#111', lineHeight: 1.2, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {ride.to || 'Drop'}
          </Typography>
          {routeMeta && (
            <Typography sx={{ ml: 0.5, fontSize: 11, color: '#888', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {routeMeta}
            </Typography>
          )}
        </Box>

        {/* ── Price/Discuss bar — unlocked only ───────────────────────── */}
        {(contactUnlocked || freeAccess) && (
          <Box sx={{ bgcolor: '#E8F5E9', borderRadius: 1.5, px: 1.25, py: 0.875, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography fontWeight={600} sx={{ fontSize: 13, color: '#1B5E20' }}>
              {priceDisplay}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75}>
              {matchInfo.label && (
                <Typography fontWeight={600} sx={{ fontSize: 11, color: '#E8A000' }}>
                  {matchInfo.label}
                </Typography>
              )}
              <SignalBars level={matchInfo.level} color={matchInfo.color} />
            </Box>
          </Box>
        )}

        {/* ── Message box ──────────────────────────────────────────────── */}
        {hasMessage && (
          <Box sx={{ bgcolor: '#F7F7F7', borderRadius: 1.5, px: 1.25, py: 0.875, border: '1px solid #EBEBEB' }}>
            <Typography sx={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.375 }}>
              {contactUnlocked ? 'Message' : 'Original Message'}
            </Typography>
            <Typography sx={{
              fontSize: 12,
              color: '#333',
              lineHeight: 1.45,
              wordBreak: 'break-word',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {tidyOriginalMessage(messageText)}
            </Typography>
          </Box>
        )}

        {/* ── Match reason chips ────────────────────────────────────────── */}
        {ride.match?.matchedReasons?.length ? (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {ride.match.matchedReasons.slice(0, 3).map((r) => (
              <Chip key={r} label={r} size="small" variant="outlined"
                sx={{ fontSize: 10, height: 16, color: '#14a35b', borderColor: 'rgba(20,163,91,0.3)', bgcolor: 'rgba(20,163,91,0.04)' }}
              />
            ))}
          </Stack>
        ) : null}

        {/* ── Contact section ───────────────────────────────────────────── */}
        {contactUnlocked ? (
          /* Green contact card */
          <Box sx={{ bgcolor: '#E8F5E9', borderRadius: 1.5, px: 1.25, py: 0.875, display: 'flex', alignItems: 'center', gap: 1.125 }}>
            <Avatar sx={{ bgcolor: '#2E7D32', width: 28, height: 28, flexShrink: 0 }}>
              <PersonIcon sx={{ fontSize: 14 }} />
            </Avatar>
            <Box>
              <Typography fontWeight={600} sx={{ fontSize: 12, color: '#1B5E20', lineHeight: 1.3 }}>
                {unlocked?.contactName || 'Rider'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#2E7D32', lineHeight: 1.3 }}>
                {unlocked?.contactNumber}
              </Typography>
            </Box>
          </Box>
        ) : freeAccess ? (
          <Box sx={{ bgcolor: '#F7F7F7', borderRadius: 1.5, px: 1.25, py: 0.875, border: '1px solid #EBEBEB' }}>
            <Typography fontWeight={600} sx={{ fontSize: 12, color: '#555' }}>
              Contact not available
            </Typography>
          </Box>
        ) : (
          /* Grey locked contact card with price-hidden inline */
          <Box sx={{ bgcolor: '#F7F7F7', borderRadius: 1.5, px: 1.25, py: 0.875, display: 'flex', alignItems: 'center', gap: 1.125, border: '1px solid #EBEBEB' }}>
            <Avatar sx={{ bgcolor: '#424242', width: 28, height: 28, flexShrink: 0 }}>
              <LockIcon sx={{ fontSize: 13, color: 'white' }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography fontWeight={600} sx={{ fontSize: 12, color: '#222', lineHeight: 1.3 }}>Ride details locked</Typography>
              <Typography sx={{ fontSize: 11, color: '#888', lineHeight: 1.3 }}>Unlock this ride to view full details</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.375} sx={{ flexShrink: 0 }}>
              {showPrice ? (
                <Typography fontWeight={700} sx={{ fontSize: 11, color: '#2E7D32', whiteSpace: 'nowrap' }}>
                  {priceDisplay}
                </Typography>
              ) : (
                <>
                  <LockIcon sx={{ fontSize: 11, color: '#999' }} />
                  <Typography sx={{ fontSize: 10, color: '#999', whiteSpace: 'nowrap' }}>Price hidden</Typography>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* ── Action buttons ─────────────────────────────────────────────── */}
        {contactUnlocked ? (
          <Stack direction="row" spacing={0.625}>
            <Button
              variant="contained"
              size="small"
              startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
              onClick={sp(handleCall)}
              sx={{ flex: 1, fontSize: 12, fontWeight: 600, py: 0.875, bgcolor: '#2E7D32', '&:hover': { bgcolor: '#1B5E20' }, borderRadius: 1.5 }}
            >
              Call
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<WhatsAppIcon sx={{ fontSize: 14 }} />}
              onClick={sp(handleWhatsApp)}
              sx={{ flex: 1, fontSize: 12, fontWeight: 600, py: 0.875, bgcolor: '#25D366', '&:hover': { bgcolor: '#1da34e' }, borderRadius: 1.5 }}
            >
              WhatsApp
            </Button>
            <Tooltip title={copied ? 'Copied!' : 'Copy number'}>
              <IconButton
                size="small"
                onClick={sp(handleCopy)}
                sx={{ width: 34, height: 34, border: '1px solid #DDD', borderRadius: 1.5, bgcolor: 'white', flexShrink: 0 }}
              >
                {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14, color: '#666' }} />}
              </IconButton>
            </Tooltip>
          </Stack>
        ) : !freeAccess && onUnlock ? (
          <Button
            variant="contained"
            fullWidth
            startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
            onClick={sp(onUnlock)}
            disabled={unlockDisabled}
            sx={{
              bgcolor: '#E8A000',
              color: 'white',
              fontWeight: 600,
              fontSize: 13,
              py: 1,
              borderRadius: 1.5,
              '&:hover': { bgcolor: '#d97706' },
              '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.12)', color: 'rgba(0,0,0,0.26)' },
            }}
          >
            {creditsRemaining !== undefined && creditsRemaining !== null
              ? `Unlock Ride · ${creditsRemaining} credit${creditsRemaining !== 1 ? 's' : ''} left`
              : 'Unlock Ride'}
          </Button>
        ) : null}

        {/* ── Footer: View details ← → Not relevant ────────────────────── */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {onDetails ? (
            <Button
              variant="text"
              size="small"
              onClick={sp(onDetails)}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
              sx={{ fontSize: 12, color: '#222', py: 0, px: 0, minWidth: 0, fontWeight: 400, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
            >
              View details
            </Button>
          ) : <Box />}
          {onReport && (
            <Button
              variant="text"
              size="small"
              onClick={sp(onReport)}
              disabled={actionDisabled || ride.reported}
              sx={{ fontSize: 12, color: '#E8A000', py: 0, px: 0, minWidth: 0, fontWeight: 500, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }, '&.Mui-disabled': { color: 'rgba(0,0,0,0.3)' } }}
            >
              {ride.reported ? 'Reported' : 'Not relevant'}
            </Button>
          )}
        </Box>

      </CardContent>
    </Card>
  );
};

export default RideCard;
